import { Video, VideoOff, Mic, MicOff, X } from 'lucide-react'
import { cn } from '@/utils/helpers'
import type { VideoState } from '@hooks/useVideoState'

type VideoControlButtonProps = {
  icon: 'video' | 'microphone'
  isActive: boolean
  onClick: () => void
  className?: string
  'aria-label': string
  audioLevel?: number
  videoState?: VideoState
  isPermissionDenied?: boolean
  buttonSize?: 'md' | 'lg'
}

export const PeripheralToggleButton = ({
  icon,
  isActive,
  onClick,
  className,
  'aria-label': ariaLabel,
  audioLevel = 0,
  videoState = 'inactive',
  isPermissionDenied = false,
  buttonSize
}: VideoControlButtonProps) => {
  const IconComponent = (() => {
    if (icon === 'video') {
      return isActive ? Video : VideoOff
    }
    return isActive ? Mic : MicOff
  })()

  const incomingMicPercentage = icon === 'microphone' && isActive ? audioLevel * 100 : 0
  const showMicPercentage =
    icon === 'microphone' ? isActive && incomingMicPercentage > 0 : isActive && videoState !== 'inactive'

  return (
    <div className="relative">
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        aria-pressed={isActive}
        className={cn(
          buttonSize === 'md' ? 'h-[52px] w-[52px]' : 'h-16 w-16',
          'relative flex items-center justify-center rounded-full transition-all duration-200',
          'backdrop-blur-sm overflow-clip',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2',
          isActive
            ? 'bg-[#ffffff]/15 hover:bg-[#ffffff]/50 text-white'
            : 'bg-gray-50/75 hover:bg-gray-50 text-foreground',
          videoState === 'error' ? 'bg-red-500' : '',
          className
        )}
      >
        {showMicPercentage && (
          <div
            className={cn(`absolute bottom-0 left-0 w-full bg-green-500/50 -z-10`)}
            style={{ height: `${incomingMicPercentage}%` }}
          />
        )}
        <IconComponent className="h-5 w-5" />
      </button>
      {/* Red X overlay when permission is denied */}
      {isPermissionDenied && (
        <div className="absolute top-2/3 left-2/3 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 shadow-sm">
          <X className="h-3 w-3 text-white" strokeWidth={3} />
        </div>
      )}
    </div>
  )
}
